import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { BusinessCategoryPicker } from '@/components/common/business-category-picker';
import { giveAskApi, giveAskHeading, type GiveAskType, type MyCommunityGiveAsk } from '@/lib/give-ask-api';

interface GivesAsksCommunitySectionProps {
  communityId: string;
  communityName: string;
}

function isFulfilledAsk(item: MyCommunityGiveAsk) {
  return item.type === 'ASK' && item.status === 'FULFILLED';
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function GivesAsksCommunitySection({ communityId, communityName }: GivesAsksCommunitySectionProps) {
  const navigate = useNavigate();
  const { data, mutate } = useSWR(['me-gives-asks', communityId], () =>
    giveAskApi.listMyCommunityItems(communityId)
  );
  const items = data?.items ?? [];
  const enabled = items.filter((item) => item.enabled);
  const others = items.filter((item) => !item.enabled);

  const [showOthers, setShowOthers] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [type, setType] = useState<GiveAskType>('GIVE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [category, setCategory] = useState<{ businessCategory: string; subCategory: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const toggle = async (target: MyCommunityGiveAsk) => {
    if (isFulfilledAsk(target)) return;
    const next = items.map((item) => (item.id === target.id ? { ...item, enabled: !item.enabled } : item));
    await mutate({ items: next }, { revalidate: false });
    try {
      await giveAskApi.setCommunityEnabled(
        communityId,
        next.filter((item) => item.enabled && !isFulfilledAsk(item)).map((item) => item.id)
      );
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update this community'));
      await mutate();
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setBusinessName('');
    setCategory(null);
    setContactName('');
    setContactPhone('');
    setContactEmail('');
    setType('GIVE');
    setEditingId(null);
  };

  const closeForm = () => {
    resetForm();
  };

  const openEdit = (item: MyCommunityGiveAsk) => {
    if (isFulfilledAsk(item)) return;
    setEditingId(item.id);
    setType(item.type);
    setTitle(item.title ?? '');
    setDescription(item.description);
    setBusinessName(item.businessName ?? '');
    setCategory(
      item.category ? { businessCategory: item.category, subCategory: item.subCategory ?? '' } : null
    );
    setContactName(item.contactName ?? '');
    setContactPhone(item.contactPhone ?? '');
    setContactEmail(item.contactEmail ?? '');
  };

  const openCreatePage = () => {
    navigate(`/${communityId}/profile/gives-asks/new`);
  };

  const update = async () => {
    if (!editingId) return;
    if (type === 'ASK' && title.trim().length < 3) {
      toast.error('Add the name of what you are looking for');
      return;
    }
    if (type === 'GIVE' && !contactName.trim() && !businessName.trim()) {
      toast.error('Add a name or a business name');
      return;
    }
    if (description.trim().length < 3) {
      toast.error('Add a short description');
      return;
    }
    setSaving(true);
    try {
      await giveAskApi.updateOwn(editingId, {
        title: type === 'ASK' ? title.trim() : undefined,
        description: description.trim(),
        businessName: businessName.trim() || null,
        category: category?.businessCategory ?? null,
        subCategory: category?.subCategory ?? null,
        contactName: type === 'GIVE' ? contactName.trim() || null : null,
        contactPhone: contactPhone.trim() || null,
        contactEmail: contactEmail.trim() || null,
      });
      toast.success(type === 'ASK' ? 'Ask updated' : 'Give updated');
      closeForm();
      await mutate();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not update'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item: MyCommunityGiveAsk) => {
    if (isFulfilledAsk(item)) return;
    if (
      !window.confirm(
        `Delete "${giveAskHeading(item)}"? It will be removed from all communities and events.`
      )
    ) {
      return;
    }
    try {
      await giveAskApi.deleteOwn(item.id);
      toast.success(item.type === 'ASK' ? 'Ask deleted' : 'Give deleted');
      if (editingId === item.id) closeForm();
      await mutate();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete'));
    }
  };

  const editForm =
    editingId != null ? (
      <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
        <div className="flex gap-2">
          <Button size="sm" variant={type === 'GIVE' ? 'default' : 'outline'} disabled>
            Give
          </Button>
          <Button size="sm" variant={type === 'ASK' ? 'default' : 'outline'} disabled>
            Ask
          </Button>
        </div>
        {type === 'ASK' ? (
          <Input
            placeholder="Who or what are you looking for?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        ) : null}
        <Textarea
          placeholder="Describe it"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
        />
        <Input
          placeholder="Business name (optional)"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
        />
        <BusinessCategoryPicker
          businessCategory={category?.businessCategory}
          subCategory={category?.subCategory}
          onSelect={setCategory}
        />
        <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Contact details
        </p>
        <Input
          placeholder="Name"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
        />
        <PhoneInput
          value={contactPhone || undefined}
          onChange={(v) => setContactPhone(v ?? '')}
          placeholder="Phone number"
          defaultCountry="IN"
        />
        <Input
          type="email"
          placeholder="Email (optional)"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={closeForm}>
            Cancel
          </Button>
          <Button onClick={() => void update()} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    ) : null;

  const row = (item: MyCommunityGiveAsk) => {
    if (editingId === item.id) {
      return (
        <li key={item.id} className="py-3">
          {editForm}
        </li>
      );
    }
    const fulfilled = isFulfilledAsk(item);
    return (
      <li key={item.id} className="flex items-center justify-between gap-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            <span className={item.type === 'ASK' ? 'text-orange-600' : 'text-teal-700'}>
              {item.type === 'ASK' ? 'Ask' : 'Give'}
            </span>{' '}
            · {giveAskHeading(item)}
            {fulfilled ? <span className="ml-2 text-xs text-emerald-700">Fulfilled</span> : null}
            {!item.enabled ? (
              <span className="ml-2 text-xs text-amber-700">Not shown in this community</span>
            ) : null}
          </p>
          <p className="truncate text-xs text-muted-foreground">{item.description}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <Switch
            checked={item.enabled}
            disabled={fulfilled}
            onCheckedChange={() => void toggle(item)}
            aria-label={`Show ${giveAskHeading(item)} in ${communityName}`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={fulfilled}
            onClick={() => openEdit(item)}
          >
            Edit
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-destructive"
            disabled={fulfilled}
            onClick={() => void remove(item)}
          >
            Delete
          </Button>
        </div>
      </li>
    );
  };

  return (
    <section className="space-y-4 rounded-2xl border bg-white p-5 shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="font-semibold text-[var(--navy)]">Gives & Asks · {communityName}</h3>
          <p className="text-sm text-muted-foreground">
            Choose what shows in this community. Changes here do not affect your other communities.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={openCreatePage}>
          <Plus className="mr-1 h-4 w-4" />
          Add new
        </Button>
      </header>

      {enabled.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing enabled for {communityName} yet.
        </p>
      ) : (
        <ul className="divide-y">{enabled.map(row)}</ul>
      )}

      {showOthers ? (
        others.length === 0 ? (
          <p className="text-sm text-muted-foreground">No other gives or asks to show.</p>
        ) : (
          <ul className="divide-y">{others.map(row)}</ul>
        )
      ) : null}

      {others.length > 0 ? (
        <Button type="button" size="sm" variant="outline" onClick={() => setShowOthers((v) => !v)}>
          {showOthers ? 'Hide other options' : 'See more options'}
        </Button>
      ) : null}
    </section>
  );
}
